/**
 * Corner styles for the organiser's public pages (Branding > Corners).
 *
 * Stored in events.metadata.landingThemeBorderRadius, the key the old two-way
 * setting used, so no migration is needed:
 *
 *   'rounded'  Rounded. Fully round buttons and chips, soft boxes. This is
 *              what every event saved as 'rounded' (and every event with no
 *              setting) already shows on its main buttons (Continue to
 *              registration, Get tickets), so those events keep their look.
 *   'soft'     Soft square. Buttons and controls a modest 8px, like the
 *              organiser dashboard; boxes 12px.
 *   'straight' Square. No rounding anywhere.
 *
 * Older or hand-written values are read leniently: 'square' is Square and
 * 'pill' is Rounded. Anything else falls back to Rounded.
 *
 * The page draws every shape from five CSS variables, set once on the themed
 * page (ThemedEventPage) so no surface needs to know which style is chosen:
 *
 *   --fv-radius-button   call-to-action and secondary buttons
 *   --fv-radius-control  inputs, selects, steppers, segmented controls
 *   --fv-radius-card     section boxes, panels, sheets and dialogs
 *   --fv-radius-tile     boxes inside a box: ticket and wave options, sponsor
 *                        tiles, rows in a list
 *   --fv-radius-chip     badges, tags, status pills and filter chips
 *
 * Shapes that are round by nature (avatars, radio dots, step numbers, status
 * dots, spinners) stay round in every style.
 */

export type CornerStyle = 'rounded' | 'soft' | 'straight';

export const CORNER_STYLES: readonly CornerStyle[] = ['rounded', 'soft', 'straight'] as const;

export interface CornerRadii {
  button: string;
  control: string;
  card: string;
  tile: string;
  chip: string;
}

const RADII: Record<CornerStyle, CornerRadii> = {
  rounded: { button: '9999px', control: '10px', card: '16px', tile: '12px', chip: '9999px' },
  soft: { button: '8px', control: '8px', card: '12px', tile: '8px', chip: '6px' },
  straight: { button: '0px', control: '0px', card: '0px', tile: '0px', chip: '0px' },
};

/** The corner style a stored value means. Unknown or absent is Rounded. */
export function cornerStyleOf(raw: unknown): CornerStyle {
  if (typeof raw !== 'string') return 'rounded';
  const v = raw.trim().toLowerCase();
  if (v === 'straight' || v === 'square') return 'straight';
  if (v === 'soft' || v === 'soft-square') return 'soft';
  return 'rounded';
}

/** The radii a stored value draws with. */
export function cornerRadiiOf(raw: unknown): CornerRadii {
  return RADII[cornerStyleOf(raw)];
}

/** The CSS custom properties, as declarations for a style block. */
export function cornerRadiusCss(raw: unknown): string {
  const r = cornerRadiiOf(raw);
  return [
    `--fv-radius-button: ${r.button};`,
    `--fv-radius-control: ${r.control};`,
    `--fv-radius-card: ${r.card};`,
    `--fv-radius-tile: ${r.tile};`,
    `--fv-radius-chip: ${r.chip};`,
  ].join(' ');
}

/** What each choice is called in Branding and the create wizard. */
export const CORNER_STYLE_LABELS: Record<CornerStyle, string> = {
  rounded: 'Rounded',
  soft: 'Soft square',
  straight: 'Square',
};

/** The choices, in picker order. */
export const CORNER_STYLE_OPTIONS: { value: CornerStyle; label: string }[] = CORNER_STYLES.map((value) => ({ value, label: CORNER_STYLE_LABELS[value] }));

/** The short tip beside the picker. */
export const CORNER_STYLE_TIP = 'Sets the shape of every button, box and field on your event pages. Rounded gives fully round buttons, Soft square a gentle curve, Square sharp corners.';
